import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { RM } from '../lib/rm';

// A restless corner-morphing, spinning, pulsing dot that ramps its energy while
// the twin is working (`active`). Colors cycle amber↔cyan.
export function HeaderBlob({ active }: { active: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  const applyActive = useRef<(on: boolean) => void>(() => {});

  useEffect(() => {
    const blob = ref.current;
    if (!blob) return;
    gsap.set(blob, { y: 1, backgroundColor: '#63E29A' });
    if (RM) return;

    const R = gsap.utils.random;
    let energy = 1;
    let killed = false;
    const tweens: gsap.core.Tween[] = [];

    // Self-restarting tweens created from onComplete are NOT tracked by
    // gsap.context, so track every generation explicitly and stop the recursion
    // via `killed` on cleanup.
    const spawn = (vars: gsap.TweenVars, loop: () => void) => {
      if (killed) return;
      tweens.push(gsap.to(blob, { ...vars, onComplete: loop }));
    };

    (
      ['borderTopLeftRadius', 'borderTopRightRadius', 'borderBottomRightRadius', 'borderBottomLeftRadius'] as const
    ).forEach((prop) => {
      const go = () => spawn({ [prop]: R(18, 86) + '%', duration: R(0.4, 0.9) / energy, ease: 'sine.inOut' }, go);
      go();
    });
    const jit = () => spawn({ x: R(-1.6, 1.6), y: R(-0.4, 2.2), duration: R(0.3, 0.6) / energy, ease: 'sine.inOut' }, jit);
    jit();

    const spin = gsap.to(blob, { rotation: 360, duration: 4.5, ease: 'none', repeat: -1 });
    const pulse = gsap.to(blob, { scale: 1.3, duration: 0.85, ease: 'sine.inOut', repeat: -1, yoyo: true });
    const color = gsap.to(blob, { backgroundColor: '#56CFC9', duration: 2.2, ease: 'sine.inOut', repeat: -1, yoyo: true });
    tweens.push(spin, pulse, color);

    applyActive.current = (on: boolean) => {
      energy = on ? 2.4 : 1;
      gsap.to([spin, pulse, color], { timeScale: on ? 2.3 : 1, duration: 0.5 });
      gsap.to(blob, {
        boxShadow: on ? '0 0 18px rgba(86,207,201,.9)' : '0 0 10px rgba(86,207,201,.5)',
        duration: 0.5,
      });
    };

    return () => {
      killed = true;
      tweens.forEach((t) => t.kill());
      gsap.killTweensOf(blob);
      applyActive.current = () => {};
    };
  }, []);

  useEffect(() => {
    applyActive.current(active);
  }, [active]);

  return <span className="blob" ref={ref} />;
}
